import React, { useCallback, useEffect, useState, useContext } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  FormText,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import Countries from "../../utils/countriesCustom.json";
import LoaderInner from '../../components/LoaderInner';
import Select from 'react-select';
import TaxRates from '../../views/taxRates/taxRates';
import TenantContext from '../../utils/tenantContext';
import { invoiceConfigurationService } from '../../jwt/_services/invoiceConfiguration.service';

const safeValue = (value) => (value || value === 0 ? value : '');

const SubuserInvoiceConfiguration = () => {
  const {tenant} = useContext(TenantContext);
  const [invoiceFooterText, setInvoiceFooterText] = useState('');
  const [invoiceCompanyName, setInvoiceCompanyName] = useState('');
  const [invoiceStreetAndNumber, setInvoiceStreetAndNumber] = useState('');
  const [invoicePostalCode, setInvoicePostalCode] = useState('');
  const [invoiceCity, setInvoiceCity] = useState('');
  const [invoiceCountry, setInvoiceCountry] = useState(false);
  const [invoiceVat, setInvoiceVat] = useState('');
  const [invoiceTaxNr, setInvoiceTaxNr] = useState('');
  const [invoiceRegistryNr, setInvoiceRegistryNr] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [subuserInvoicesEnabled, setSubuserInvoicesEnabled] = useState(false);

  const getCountryCode = (countryValue) => {
    if (!countryValue) return '';
    if (typeof countryValue === 'string') return countryValue;
    if (Array.isArray(countryValue)) return countryValue?.[0]?.value || '';
    return countryValue?.value || '';
  };

  const loadConfig = useCallback(() => {
    setLoading(true);
    setError('');
    invoiceConfigurationService
      .fetchConfig()
      .then((data) => {
        setInvoiceFooterText(safeValue(data?.invoice_footer_text));
        setInvoiceCompanyName(safeValue(data?.invoice_company_name));
        setInvoiceStreetAndNumber(safeValue(data?.invoice_street_and_number));
        setInvoicePostalCode(safeValue(data?.invoice_postal_code));
        setInvoiceCity(safeValue(data?.invoice_city));
        if (data?.invoice_country) {
          setInvoiceCountry(Countries.filter((i) => i.value === data.invoice_country));
        } else {
          setInvoiceCountry(false);
        }
        setInvoiceVat(safeValue(data?.invoice_vat));
        setInvoiceTaxNr(safeValue(data?.invoice_tax_nr));
        setInvoiceRegistryNr(safeValue(data?.invoice_registry_nr));
      })
      .catch(() => {
        // setError('Failed to load invoice configuration.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (tenant && tenant.invoices_enabled) {
      setSubuserInvoicesEnabled(true);
    }
  }, [tenant]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    setSaving(true);
    invoiceConfigurationService
      .updateConfig({
        invoice_footer_text: invoiceFooterText || '',
        invoice_company_name: invoiceCompanyName || '',
        invoice_street_and_number: invoiceStreetAndNumber || '',
        invoice_postal_code: invoicePostalCode || '',
        invoice_city: invoiceCity || '',
        invoice_country: getCountryCode(invoiceCountry),
        invoice_vat: invoiceVat || '',
        invoice_tax_nr: invoiceTaxNr || '',
        invoice_registry_nr: invoiceRegistryNr || '',
      })
      .then((data) => {
        setSuccess(true);
        setInvoiceFooterText(safeValue(data?.invoice_footer_text));
        setInvoiceCompanyName(safeValue(data?.invoice_company_name));
        setInvoiceStreetAndNumber(safeValue(data?.invoice_street_and_number));
        setInvoicePostalCode(safeValue(data?.invoice_postal_code));
        setInvoiceCity(safeValue(data?.invoice_city));
        if (data?.invoice_country) {
          setInvoiceCountry(Countries.filter((i) => i.value === data.invoice_country));
        } else {
          setInvoiceCountry(false);
        }
        setInvoiceVat(safeValue(data?.invoice_vat));
        setInvoiceTaxNr(safeValue(data?.invoice_tax_nr));
        setInvoiceRegistryNr(safeValue(data?.invoice_registry_nr));
      })
      .catch(() => {
        setError('Unable to save invoice configuration.');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div>
      <LoaderInner show={loading || saving} />
        {!subuserInvoicesEnabled && (
          <Alert color="warning">
            Subuser invoices are offered with <Link to="/sla">Gold Plan SLA</Link> only. Please <Link to="/sla">upgrade your account</Link> in order to avail this feature.
          </Alert>
        )}
        <Card className="mb-4">
          <CardHeader>
            <h5 className="mb-0">Invoice Details</h5>
          </CardHeader>
          <CardBody>
            <Form onSubmit={handleSave}>
              <div className="mb-4">
                <Row form="true">
                  <Col md={12}>
                    <FormGroup>
                      <Label for="invoiceCompanyName">Company name</Label>
                      <Input
                        id="invoiceCompanyName"
                        type="text"
                        name="invoice_company_name"
                        value={invoiceCompanyName}
                        onChange={(e) => setInvoiceCompanyName(e.target.value)}
                        disabled={loading || saving || !subuserInvoicesEnabled}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label for="invoiceStreetAndNumber">Street and number</Label>
                      <Input
                        id="invoiceStreetAndNumber"
                        type="text"
                        name="invoice_street_and_number"
                        value={invoiceStreetAndNumber}
                        onChange={(e) => setInvoiceStreetAndNumber(e.target.value)}
                        disabled={loading || saving || !subuserInvoicesEnabled}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label for="invoicePostalCode">Postal code</Label>
                      <Input
                        id="invoicePostalCode"
                        type="text"
                        name="invoice_postal_code"
                        value={invoicePostalCode}
                        onChange={(e) => setInvoicePostalCode(e.target.value)}
                        disabled={loading || saving || !subuserInvoicesEnabled}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label for="invoiceCity">City</Label>
                      <Input
                        id="invoiceCity"
                        type="text"
                        name="invoice_city"
                        value={invoiceCity}
                        onChange={(e) => setInvoiceCity(e.target.value)}
                        disabled={loading || saving || !subuserInvoicesEnabled}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label>Country</Label>
                      <Select
                        components={{ IndicatorSeparator:() => null }}
                        name="country"
                        options={Countries}
                        value={invoiceCountry}
                        onChange={(e) => setInvoiceCountry(e)}
                        isDisabled={!subuserInvoicesEnabled}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label for="invoiceVat">VAT</Label>
                      <Input
                        id="invoiceVat"
                        type="text"
                        name="invoice_vat"
                        value={invoiceVat}
                        onChange={(e) => setInvoiceVat(e.target.value)}
                        disabled={loading || saving || !subuserInvoicesEnabled}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label for="invoiceTaxNr">Tax number</Label>
                      <Input
                        id="invoiceTaxNr"
                        type="text"
                        name="invoice_tax_nr"
                        value={invoiceTaxNr}
                        onChange={(e) => setInvoiceTaxNr(e.target.value)}
                        disabled={loading || saving || !subuserInvoicesEnabled}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup className="mb-0">
                      <Label for="invoiceRegistryNr">Registry number</Label>
                      <Input
                        id="invoiceRegistryNr"
                        type="text"
                        name="invoice_registry_nr"
                        value={invoiceRegistryNr}
                        onChange={(e) => setInvoiceRegistryNr(e.target.value)}
                        disabled={loading || saving || !subuserInvoicesEnabled}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup className="mt-3 mb-0">
                      <Label for="footerText">Footer Text</Label>
                      <Input
                        id="footerText"
                        name="footerText"
                        type="textarea"
                        rows="4"
                        value={invoiceFooterText}
                        onChange={(e) => setInvoiceFooterText(e.target.value)}
                        disabled={loading || saving || !subuserInvoicesEnabled}
                        placeholder="Add custom footer text"
                      />
                      <FormText color="muted">
                        This text appears at the bottom of all invoices
                      </FormText>
                    </FormGroup>
                  </Col>
                </Row>
              </div>

              <div className="d-flex justify-content-end">
                <Button color="primary" type="submit" disabled={loading || saving || !subuserInvoicesEnabled}>
                  Save
                </Button>
              </div>
            </Form>
            <div className="mt-3">
              {error && <Alert color="danger">{error}</Alert>}
              {success && <Alert color="success">Invoice configuration saved successfully.</Alert>}
            </div>
          </CardBody>
        </Card>
        <TaxRates subuserInvoicesEnabled={subuserInvoicesEnabled} />
    </div>
  );
};

export default SubuserInvoiceConfiguration;
